import { FormControl, FormControlLabel, Radio, RadioGroup } from "@mui/material";
import clsx from "clsx";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./right-to-know.css";
import { ExternalLink } from "../../../../../../common/components/ExternalLink";
import { InlineLoading } from "../../../../../../common/components/Loading";
import { useActionRequest } from "../../../../../../common/hooks/api";
import { Api } from "../../../../../../common/service/Api";
import { usePrimaryOrganization } from "../../../../../hooks/useOrganization";
import { useOrganizationVendors } from "../../../../../hooks/useOrganizationVendors";
import { sortVendorsByCategory } from "../../../../../util/vendorCategorization";
import { Callout, CalloutVariant } from "../../../../Callout";
import { DataRecipientLogo } from "../../../../DataRecipientLogo";
import { CategorizedVendorList } from "../shared/CategorizedVendorList";
import { InstructionsButton, InstructionsContent } from "../shared/CollapsibleInstructions";
import { useInstructionsState } from "../shared/useInstructionsState";
import { SensitiveInfoCallout } from "../../../../dialog/SensitiveInfoDialog";
import { useDataRequestProcessingContext } from "../../DataRequestProcessingContext";
import { useSurvey } from "../../../../../../common/hooks/api/useSurvey";
import { SELLING_AND_SHARING_SURVEY_NAME } from "../../../../../pages/get-compliant/data-recipients/shared";
import {
  getRequestProcessingRegion,
  isRequestContextApplicableWithCrunchFiltering,
} from "../../../../../util/vendors";
import { useDataMap } from "../../../../../../common/hooks/api/useDataMap";
import { getSensitivePICsWithFilters } from "../../../../../../common/service/resources/data-map/picHelpers";
import { useDataMapCollectedPIC } from "../../../../../../common/service/resources/data-map/dataMapTransformers";
import { PersonalIdentifiersForm, PersonalIdentifiersData } from "./PersonalIdentifiersForm";
import { StickyFileDropper } from "../shared/StickyFileDropper";
import { useDataRequestAttachments } from "../../../../../hooks/useDataRequestAttachments";
import { AlertCard } from "@truevault/aquilo";

const normalizePersonalIdentifiers = (data: PersonalIdentifiersData): PersonalIdentifiersData => ({
  firstName: (data.firstName || "").trim(),
  lastName: (data.lastName || "").trim(),
  email: (data.email || "").trim(),
  phone: (data.phone || "").trim(),
  subjectFreePhone: (data.subjectFreePhone || "").trim(),
  postalAddress: (data.postalAddress || "").trim(),
  subjectFreeTextAddress: (data.subjectFreeTextAddress || "").trim(),
  onlineIdentifiers: (data.onlineIdentifiers || "").trim(),
  customFields: data.customFields || [],
});

// Helper to extract custom fields from request
const extractCustomFieldsFromRequest = (request: any): Array<{ label: string; value: string }> => {
  const customFields: Array<{ label: string; value: string }> = [];
  const excludedKeys = new Set([
    "address1",
    "address2",
    "city",
    "state",
    "zipCode",
    "country",
    "phoneNumber",
  ]);

  // Check for legacy organizationCustomInput field
  if (request.organizationCustomInput) {
    customFields.push({
      label: "Custom Field",
      value: request.organizationCustomInput,
    });
  }

  // Add custom fields from subjectCustomFieldsFmt
  if (request.subjectCustomFieldsFmt) {
    Object.entries(request.subjectCustomFieldsFmt).forEach(([label, value]) => {
      const keyToCheck = label
        .toLowerCase()
        .replace(/\s+/g, "")
        .replace(/[()]/g, "")
        .replace(/line1/g, "1")
        .replace(/line2/g, "2");

      const isExcluded =
        excludedKeys.has(keyToCheck) ||
        label.toLowerCase().includes("address") ||
        label.toLowerCase().includes("city") ||
        label.toLowerCase().includes("state") ||
        label.toLowerCase().includes("zip") ||
        label.toLowerCase().includes("country") ||
        label.toLowerCase().includes("phone");

      if (!isExcluded && value) {
        customFields.push({ label, value: String(value) });
      }
    });
  }

  return customFields;
};

export const RetrieveInfo = () => {
  const [org] = usePrimaryOrganization();

  const [allVendors] = useOrganizationVendors(org.id, undefined, true);

  const { request, refreshRequest, setStepCompleted, setUpdateRequests, setPersonalIdentifiers } =
    useDataRequestProcessingContext();
  const isPortabilityRequest = request.dataRequestType?.workflowType === "PORTABILITY";

  const { attachments, uploadFile, deleteFile, isUploading } = useDataRequestAttachments(
    org.id,
    request.id,
  );
  const [vendorOutcomes, setVendorOutcomes] = useState<Record<string, string>>({});
  const instructionsState = useInstructionsState();
  const [customIdentifierRowCount, setCustomIdentifierRowCount] = useState<number>(0);

  const latestPersistedIdentifiersRef = useRef<PersonalIdentifiersData | null>(null);

  const { data: dataMap } = useDataMap(org.id);

  const { fetch: updateVendorOutcome, request: updateOutcomeRequest } = useActionRequest({
    api: async ({ values, resetState }) => {
      try {
        const resp = await Api.dataRequest.updateVendor(org.id, request.id, values);
        if (resp.id == undefined) {
          resetState();
        }
        refreshRequest();
      } catch (err) {
        resetState();
        throw err;
      }
    },
    allowConcurrent: true,
    onError: refreshRequest,
  });

  const onSelectionChange = (vendor, value) => {
    const resetState = () =>
      setVendorOutcomes((prev) => Object.assign({}, prev, { [vendor.id]: vendorOutcomes }));

    setVendorOutcomes((prev) => Object.assign({}, prev, { [vendor.vendorId]: value }));
    updateVendorOutcome({
      values: { vendor: vendor.vendorId, outcome: value },
      resetState: resetState,
    });
  };

  const { answers } = useSurvey(org.id, SELLING_AND_SHARING_SURVEY_NAME);

  const requestProcessingRegion = getRequestProcessingRegion(request);
  const vendors = useMemo(() => {
    return allVendors?.filter((v) => {
      const matchesProcessingRegion = v.processingRegions.includes(requestProcessingRegion);
      const instruction = v.instructions.find((vi) => vi.requestType == "KNOW");
      const instructionOk = instruction?.processingMethod != "INACCESSIBLE_OR_NOT_STORED";

      const contextOk = isRequestContextApplicableWithCrunchFiltering(
        dataMap,
        request.context,
        v,
        request,
        org,
      );
      return matchesProcessingRegion && instructionOk && contextOk;
    });
  }, [allVendors, answers, dataMap, request.context, org, request, requestProcessingRegion]);

  // set the update requests
  useEffect(() => {
    setUpdateRequests(updateOutcomeRequest);
  }, [updateOutcomeRequest]);

  useEffect(() => {
    const hasVendors = Boolean(vendors);
    const everyVendorHasOutcome = vendors?.every((v) => {
      const outcome = request.vendorOutcomes.find((vo) => vo.vendor.id == v.vendorId);
      return outcome && outcome.outcome != null;
    });

    setStepCompleted(hasVendors && everyVendorHasOutcome);
  }, [request, setStepCompleted, vendors]);

  useEffect(() => {
    const vendorOutcomes = request.vendorOutcomes.reduce((acc, outcome) => {
      return Object.assign({}, acc, { [outcome.vendor.id]: outcome.outcome });
    }, {});
    setVendorOutcomes((prev) => Object.assign({}, prev, vendorOutcomes));
  }, [request, vendors]);

  const sortedVendors = useMemo(() => {
    return sortVendorsByCategory(vendors ?? []);
  }, [vendors]);

  // Get collected PICs from consumer collection groups only (exclude HR groups)
  const collectedPICs = useDataMapCollectedPIC(dataMap, {
    collectionGroupType: ["BUSINESS_TO_CONSUMER"],
  });

  const collectedSensitiveCategories = useMemo(() => {
    if (!dataMap || !collectedPICs || !Array.isArray(collectedPICs)) return [];

    // Filter to only sensitive PICs
    const collectedPICIds = collectedPICs.map((pic) => pic?.id).filter(Boolean);
    if (collectedPICIds.length === 0) return [];

    const sensitivePICs = getSensitivePICsWithFilters(dataMap, {
      filterPicIds: collectedPICIds,
    });

    return sensitivePICs?.map((pic) => pic?.name).filter(Boolean) || [];
  }, [dataMap, collectedPICs]);

  useEffect(() => {
    latestPersistedIdentifiersRef.current = normalizePersonalIdentifiers({
      firstName: request.subjectFirstName || "",
      lastName: request.subjectLastName || "",
      email: request.subjectEmailAddress || "",
      phone: request.subjectPhoneNumber || "",
      subjectFreePhone: request.subjectFreePhone || "",
      postalAddress: request.subjectMailingAddress || "",
      subjectFreeTextAddress: request.subjectFreeTextAddress || "",
      onlineIdentifiers: latestPersistedIdentifiersRef.current?.onlineIdentifiers || "",
      customFields: extractCustomFieldsFromRequest(request),
    });
  }, [
    request.subjectFirstName,
    request.subjectLastName,
    request.subjectEmailAddress,
    request.subjectPhoneNumber,
    request.subjectFreePhone,
    request.subjectMailingAddress,
    request.subjectFreeTextAddress,
    request.subjectCustomFieldsFmt,
    request.organizationCustomInput,
  ]);

  const persistPersonalIdentifiers = useCallback(
    async (data: PersonalIdentifiersData) => {
      const normalized = normalizePersonalIdentifiers(data);
      const previous = latestPersistedIdentifiersRef.current;

      latestPersistedIdentifiersRef.current = normalized;

      try {
        await Api.dataRequest.updateRequest(org.id, request.id, {
          requestDate: request.requestDate,
          subjectFirstName: normalized.firstName,
          subjectLastName: normalized.lastName,
          subjectEmailAddress: normalized.email,
          // Only save to "free" fields to prevent retroactive locking
          // subjectPhoneNumber and subjectMailingAddress should remain unchanged (they hold original pulled-in data)
          subjectFreePhone: normalized.phone,
          subjectFreeTextAddress: normalized.postalAddress,
          subjectOnlineIdentifiers: normalized.onlineIdentifiers,
          submissionMethod: request.submissionMethod || "MANUAL",
          dataRequestType: request.dataRequestType,
        });
        refreshRequest();
      } catch (error) {
        latestPersistedIdentifiersRef.current = previous;
        console.error("[RetrieveInfo] Failed to save personal identifiers:", error);
      }
    },
    [
      org.id,
      request.id,
      request.requestDate,
      request.submissionMethod,
      request.dataRequestType,
      refreshRequest,
    ],
  );

  const handlePersonalIdentifiersBlur = useCallback(
    (data: PersonalIdentifiersData) => {
      setPersonalIdentifiers(data);
      void persistPersonalIdentifiers(data);
    },
    [setPersonalIdentifiers, persistPersonalIdentifiers],
  );

  return (
    <>
      <div className="text-t3 mb-std">
        {isPortabilityRequest ? (
          <>
            Download files containing the consumer&apos;s personal data from your Data Recipients
            below that provide data in a portable format, such as a CSV or PDF. For any systems that
            do not offer a way to download the consumer&apos;s data in a portable document, select
            "No data to retrieve."
          </>
        ) : (
          <>
            Retrieve specific personal information you have about the consumer. Attach data files or
            type in data collected below. Your defaults and instructions can be updated in Request
            Instructions.
          </>
        )}
      </div>
      {isPortabilityRequest && (
        <Callout
          collapsible
          title={<span className="text-t2">What is the Right to Portability?</span>}
          variant={CalloutVariant.Clear}
        >
          The right to data portability allows individuals to obtain and reuse their personal data
          for their own purposes across different services. It allows them to move, copy or transfer
          personal data easily from one IT environment to another in a safe and secure way, without
          affecting its usability.
        </Callout>
      )}
      <div className="mb-std flex flex-col gap-3">
        {collectedSensitiveCategories.length > 0 && (
          <SensitiveInfoCallout sensitivePICs={collectedSensitiveCategories} />
        )}
        <div className="tw-boundary">
          <AlertCard variant="info" containerClasses="mt-0 mb-0">
            <div className="text-t3">
              <strong>Remember to include inferences.</strong> If applicable, include any inferences
              your organization has made about this consumer (e.g., predicted preferences, scores,
              or categories derived from their data).
            </div>
          </AlertCard>
        </div>
      </div>
      <div className="mb-std flex flex-row gap-4 items-stretch">
        <div className="flex-1">
          <Callout variant={CalloutVariant.WhiteDropShadow}>
            <div className="know-section-title">Consumer's Collected Data</div>
            <div className="know-section-subtitle">
              These are Personal Identifiers your business collects.
            </div>
            <PersonalIdentifiersForm
              request={request}
              organization={org}
              onChange={handlePersonalIdentifiersBlur}
              onCustomIdentifierRowCountChange={setCustomIdentifierRowCount}
            />
          </Callout>
        </div>

        {/* File Upload Section */}
        <div className="flex-1 flex flex-col">
          <StickyFileDropper
            onFileUpload={uploadFile}
            uploadedFiles={attachments.map((att) => ({
              id: att.id,
              filename: att.filename,
              size: att.size,
              status: att.status,
              uploadedAt: att.uploadedAt,
              url: att.url,
            }))}
            onFileDelete={deleteFile}
            disabled={isUploading}
            customIdentifierRowCount={customIdentifierRowCount}
          />
        </div>
      </div>

      {sortedVendors && (
        <CategorizedVendorList
          vendors={sortedVendors}
          renderVendor={(vendor, isLast, isFirstInCategory) => {
            const instruction = vendor.instructions.find((vi) => vi.requestType == "KNOW");

            const localOutcome = vendorOutcomes[vendor.vendorId] || "";
            const remoteOutcome =
              request.vendorOutcomes.find((vo) => vo.vendor.id == vendor.vendorId)?.outcome || "";

            return (
              <div key={vendor.id}>
                {!isFirstInCategory && <hr />}
                <div
                  key={`vendor-${vendor.id}`}
                  className={"flex-container pt-std pb-std border-top--grey"}
                >
                  <div className="text-t4 w-256 flex-container flex--align-start pl-xs">
                    <DataRecipientLogo
                      className="w-space-large align-top mr-mdlg"
                      dataRecipient={vendor}
                    />
                    <span>{vendor.name}</span>
                  </div>
                  <div className="flex-grow">
                    <FormControl component="fieldset" className={"mb-md"}>
                      <RadioGroup
                        aria-label="Vendor handling response"
                        className="align-items-center know-radio-group"
                        name={`vendor-${vendor.id}-data-handling`}
                        onChange={(e) => {
                          onSelectionChange(vendor, e.target.value);
                        }}
                        value={localOutcome}
                        row
                      >
                        <FormControlLabel
                          value="NO_DATA"
                          control={<Radio />}
                          label={
                            <span className="text-t2 pr-xs know-radio-label">
                              No data to retrieve
                            </span>
                          }
                          className={clsx({
                            highlight:
                              instruction?.processingMethod == "INACCESSIBLE_OR_NOT_STORED",
                          })}
                        />
                        <FormControlLabel
                          control={<Radio />}
                          value="DATA_RETRIEVED"
                          label={
                            <span className="text-t2 pr-xs know-radio-label">Data retrieved</span>
                          }
                          className={clsx({
                            highlight:
                              instruction?.processingMethod != "INACCESSIBLE_OR_NOT_STORED",
                          })}
                        />
                        <FormControlLabel
                          control={<Radio />}
                          value="DATA_IN_FORM"
                          label={
                            <span className="text-t2 pr-xs know-radio-label">
                              Data in form (above)
                            </span>
                          }
                        />

                        <InstructionsButton
                          vendorId={vendor.vendorId}
                          hasInstructions={Boolean(
                            vendor.accessInstructions || instruction?.processingInstructions,
                          )}
                          isExpanded={instructionsState.isExpanded(vendor.vendorId)}
                          onToggle={instructionsState.toggleInstructions}
                        />

                        {localOutcome != remoteOutcome && (
                          <div className="know-loading-indicator">
                            <InlineLoading />
                          </div>
                        )}
                      </RadioGroup>
                    </FormControl>

                    {vendor.ccpaRequestToKnowLink && (
                      <Callout
                        variant={CalloutVariant.Gray}
                        title="Access Instructions"
                        className="mb-md"
                      >
                        <ExternalLink
                          href={vendor.ccpaRequestToKnowLink}
                          target="_blank"
                          className="text-style-normal"
                        >
                          {vendor.name} Instructions
                        </ExternalLink>
                      </Callout>
                    )}
                  </div>
                </div>

                {/* Instructions content appears below the vendor row */}
                <InstructionsContent
                  instructions={vendor.accessInstructions}
                  processingInstructions={instruction?.processingInstructions}
                  title="Access Instructions"
                  isExpanded={instructionsState.isExpanded(vendor.vendorId)}
                />
              </div>
            );
          }}
        />
      )}
    </>
  );
};
